import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Grid } from 'semantic-ui-react';
import { connect, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { EditMode, PLanningMode } from '../../HelperMethods/constants';
import { RESOURCE_TYPE, RESOURCES } from '../../HelperMethods/role-resource-constants';
import ShouldComponentRender from './ShouldComponentRender';
import MenuComponent from './CustomMenu';
import MultiSelectDropdownMenu from './MultiSelectDropdownMenu';
import { getFieldsAndAreasAccessibleToUser } from '../../HelperMethods/UserUtilities';
import { onAreaSelected, onFieldChange, onTaskTypeGroupSelected } from '../../actions/appActions';

function shouldAreaContainerBeVisible(location, tasksEditMode, tasksPlanningState, allAreas) {
  const flag =
    (location.pathname.indexOf('taskViewPage') > -1 || location.pathname.indexOf('assetlist') > -1) &&
    tasksEditMode === EditMode.PUBLISHED &&
    tasksPlanningState === PLanningMode.PLANNINGPUBLISHED &&
    allAreas &&
    allAreas.length > 0;
  return flag;
}

function shouldTaskTypeGroupsContainerBeVisible(location, tasksEditMode, tasksPlanningState, taskTypeGroups) {
  const flag =
    location.pathname.indexOf('taskViewPage') > -1 &&
    tasksEditMode === EditMode.PUBLISHED &&
    tasksPlanningState === PLanningMode.PLANNINGPUBLISHED &&
    taskTypeGroups &&
    taskTypeGroups.length > 0;
  return flag;
}

function shouldFieldContainerBeVisible(location, tasksEditMode, tasksPlanningState) {
  const flag =
    location.pathname.indexOf('/usermanagement/') !== 0 &&
    tasksEditMode === EditMode.PUBLISHED &&
    tasksPlanningState === PLanningMode.PLANNINGPUBLISHED;

  return flag;
}

const HeaderPage = React.memo(function HeaderPage(props) {
  const location = useLocation();
  const dispatch = useDispatch();
  const {
    selectedField,
    allAreas: areaOptions,
    selectedAreas,
    allFields,
    tasksEditMode,
    tasksPlanningState,
    tasksPlanningMessage,
    loggedInUser,
    selectedTaskTypeGroups,
    taskTypeGroups: taskTypeGroupsOptions,
  } = props;
  const fieldOptions = React.useMemo(() => {
    const accessToFieldAndAreas = getFieldsAndAreasAccessibleToUser(loggedInUser);
    const isTaskViewPageOpen = location.pathname.includes('taskViewPage');
    let fieldsAccessibleToUser = allFields;
    if (isTaskViewPageOpen) {
      fieldsAccessibleToUser = allFields.filter((field) => accessToFieldAndAreas[field.id]?.length > 0);
    }
    return fieldsAccessibleToUser;
  }, [allFields, loggedInUser, location]);
  const onFieldChangeFromDropdown = (field) => {
    dispatch(onFieldChange(field));
  };
  const onAreaChanged = (areaIds) => {
    dispatch(onAreaSelected(areaIds));
  };
  const onTaskTypeGroupChanged = (taskTypeGroupIds) => {
    dispatch(onTaskTypeGroupSelected(taskTypeGroupIds));
  };
  return (
    <Grid style={{ margin: '0px', padding: '0px', borderBottom: '1px solid #e8e8e8' }}>
      <Grid.Row style={{ margin: '0px', padding: '0px' }}>
        <Grid.Column width={16} style={{ margin: '0px', padding: '0px' }}>
          <div className="nav-row" style={{ paddingTop: '10px' }}>
            <ShouldComponentRender resourceType={RESOURCE_TYPE.VIEW} resource={RESOURCES.VIEW.TASK_VIEW}>
              <NavLink to="/taskViewPage">
                <div className="nav-bar-item">Task View</div>
              </NavLink>
            </ShouldComponentRender>
            <ShouldComponentRender
              resourceType={RESOURCE_TYPE.VIEW}
              resource={[RESOURCES.VIEW.TEAM_VIEW, RESOURCES.VIEW.AREA_VIEW, RESOURCES.VIEW.TASK_TYPE_VIEW]}
            >
              <NavLink to="/taskSetup">
                <div className="nav-bar-item">Task Setup</div>
              </NavLink>
            </ShouldComponentRender>
            <ShouldComponentRender resourceType={RESOURCE_TYPE.VIEW} resource={RESOURCES.VIEW.USER_MANAGEMENT_VIEW}>
              <NavLink to="/usermanagement">
                <div className="nav-bar-item">User Mgmt</div>
              </NavLink>
            </ShouldComponentRender>
            <ShouldComponentRender resourceType={RESOURCE_TYPE.VIEW} resource={RESOURCES.VIEW.LOGS_VIEW}>
              <NavLink to="/logs">
                <div className="nav-bar-item">Logs</div>
              </NavLink>
            </ShouldComponentRender>
            <ShouldComponentRender resourceType={RESOURCE_TYPE.VIEW} resource={RESOURCES.VIEW.FORM_MANAGEMENT_VIEW}>
              <NavLink to="/formmanagement">
                <div className="nav-bar-item">Form Mgmt</div>
              </NavLink>
            </ShouldComponentRender>
            <ShouldComponentRender
              resourceType={RESOURCE_TYPE.VIEW}
              resource={RESOURCES.VIEW.ENGINE_SCHEDULE_MANAGEMENT_VIEW}
            >
              <NavLink to="/engineschedulemanagement">
                <div className="nav-bar-item">Engine Schedule Mgmt</div>
              </NavLink>
            </ShouldComponentRender>
            <ShouldComponentRender
              resourceType={RESOURCE_TYPE.VIEW}
              resource={[RESOURCES.VIEW.EXPORT_MANGEMENT_VIEW, RESOURCES.VIEW.CUSTOM_REPORTS_VIEW]}
            >
              <NavLink to="/reports" style={{ position: 'relative' }}>
                <div className="nav-bar-item beta-tag">Reports</div>
              </NavLink>
            </ShouldComponentRender>
            <NavLink to="/generalconfig">
              <div className="nav-bar-item">General Config</div>
            </NavLink>
            <ShouldComponentRender
              resourceType={RESOURCE_TYPE.VIEW}
              resource={RESOURCES.VIEW.PRIORITIZATION_MANAGEMENT_VIEW}
            >
              <NavLink to="/prioritizationconfig">
                <div className="nav-bar-item">Prioritization Config</div>
              </NavLink>
            </ShouldComponentRender>
            <ShouldComponentRender resourceType={RESOURCE_TYPE.VIEW} resource={RESOURCES.VIEW.ASSET_MANAGEMENT_VIEW}>
              <NavLink to="/assetmanagement">
                <div className="nav-bar-item">Asset Mgmt</div>
              </NavLink>
            </ShouldComponentRender>
            <div style={{ display: 'flex', float: 'right' }}>
              {tasksPlanningState !== PLanningMode.PLANNINGPUBLISHED && (
                <div
                  style={{
                    border: '1px solid rgb(51, 217, 201)',
                    padding: '2px 5px',
                    borderRadius: '4px',
                    position: 'relative',
                    top: '-3px',
                    marginRight: '20px',
                    display: 'flex',
                    color: 'rgb(51, 217, 201)',
                  }}
                >
                  {tasksPlanningState === PLanningMode.PLANNINGINPROGRESS && <i className="fa fa-spinner fa-spin" />}
                  {tasksPlanningState === PLanningMode.PLANNINGUNPUBLISHED && <i className="fa fa-check" />}

                  <span
                    style={{
                      marginLeft: '5px',
                      fontSize: '12px',
                      fontFamily: 'JoynFontBook',
                    }}
                  >
                    {tasksPlanningMessage}
                  </span>
                </div>
              )}

              {shouldFieldContainerBeVisible(location, tasksEditMode, tasksPlanningState) && (
                <SelectFieldContainer
                  allFields={fieldOptions}
                  selectedField={selectedField}
                  setFieldAsSelected={onFieldChangeFromDropdown}
                  loggedInUser={loggedInUser}
                />
              )}

              {shouldAreaContainerBeVisible(location, tasksEditMode, tasksPlanningState, areaOptions) && (
                <>
                  <SelectAreaContainer
                    allAreas={areaOptions}
                    selectedAreas={selectedAreas}
                    onAreaChanged={onAreaChanged}
                  />
                </>
              )}

              {shouldTaskTypeGroupsContainerBeVisible(
                location,
                tasksEditMode,
                tasksPlanningState,
                taskTypeGroupsOptions
              ) && (
                <>
                  <SelectTaskTypeGroupContainer
                    taskTypeGroups={taskTypeGroupsOptions}
                    selectedTaskTypeGroups={selectedTaskTypeGroups}
                    onTaskTypeGroupChanged={onTaskTypeGroupChanged}
                  />
                </>
              )}
            </div>
          </div>
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
});

const fieldPropType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
});

const areaPropType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
});

const taskTypeGroupPropType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
});

const userPropType = PropTypes.shape({
  id: PropTypes.string.isRequired,
  firstName: PropTypes.string.isRequired,
  lastName: PropTypes.string.isRequired,
});

HeaderPage.propTypes = {
  selectedField: fieldPropType.isRequired,
  allAreas: PropTypes.arrayOf(areaPropType).isRequired,
  selectedAreas: PropTypes.arrayOf(areaPropType).isRequired,
  taskTypeGroups: PropTypes.arrayOf(taskTypeGroupPropType).isRequired,
  selectedTaskTypeGroups: PropTypes.arrayOf(taskTypeGroupPropType).isRequired,
  allFields: PropTypes.arrayOf(fieldPropType).isRequired,
  tasksEditMode: PropTypes.oneOf(Object.values(EditMode)).isRequired,
  tasksPlanningState: PropTypes.oneOf(Object.values(PLanningMode)).isRequired,
  tasksPlanningMessage: PropTypes.string.isRequired,
  loggedInUser: userPropType.isRequired,
};

function getDropdownLabel(selectedIds, alloptions, labelName) {
  if (!selectedIds.length) {
    return `Select ${labelName}`;
  }
  if (selectedIds.length === 1) {
    const selected = alloptions.find((a) => a.id === selectedIds[0]);
    return selected?.name;
  }
  return `${selectedIds.length} ${labelName.toLowerCase()}s selected`;
}

const SelectAreaContainer = React.memo(function SelectAreaContainer({ selectedAreas, allAreas, onAreaChanged }) {
  const selectedAreaIds = selectedAreas.map((a) => a.id);
  return (
    <>
      <label style={{ margin: '2px 12px 0 12px' }}>Area</label>
      <div style={{ margin: '-6px 10px 0px' }}>
        <MultiSelectDropdownMenu
          selected={selectedAreaIds}
          options={(allAreas || []).sort((a, b) => a?.text?.toLowerCase() - b?.text?.toLowerCase())}
          onDropdownClose={onAreaChanged}
          label={getDropdownLabel(selectedAreaIds, allAreas, 'Area')}
        />
      </div>
    </>
  );
});

SelectAreaContainer.propTypes = {
  selectedAreas: PropTypes.arrayOf(areaPropType).isRequired,
  allAreas: PropTypes.arrayOf(areaPropType).isRequired,
  onAreaChanged: PropTypes.func.isRequired,
};

const SelectTaskTypeGroupContainer = React.memo(function SelectTaskTypeGroupContainer({
  selectedTaskTypeGroups,
  taskTypeGroups,
  onTaskTypeGroupChanged,
}) {
  const selectedTaskTypeGroupIds = selectedTaskTypeGroups.map((a) => a.id);
  return (
    <>
      <label style={{ margin: '2px 12px 0 12px' }}>Task Type Group</label>
      <div style={{ margin: '-6px 10px 0px' }}>
        <MultiSelectDropdownMenu
          selected={selectedTaskTypeGroupIds}
          options={taskTypeGroups}
          onDropdownClose={onTaskTypeGroupChanged}
          label={getDropdownLabel(selectedTaskTypeGroupIds, taskTypeGroups, 'Group')}
        />
      </div>
    </>
  );
});

SelectTaskTypeGroupContainer.propTypes = {
  selectedTaskTypeGroups: PropTypes.arrayOf(taskTypeGroupPropType).isRequired,
  taskTypeGroups: PropTypes.arrayOf(taskTypeGroupPropType).isRequired,
  onTaskTypeGroupChanged: PropTypes.func.isRequired,
};

const SelectFieldContainer = React.memo(function SelectFieldContainer({
  selectedField,
  allFields,
  setFieldAsSelected,
}) {
  return (
    <>
      <label style={{ marginTop: '2px', marginRight: '12px' }}>Field</label>
      <div style={{ margin: '-6px 10px 0px' }}>
        <MenuComponent
          selected={selectedField}
          options={allFields}
          clickHandler={setFieldAsSelected}
          label={selectedField && selectedField.name ? selectedField.name : 'Field Name'}
        />
      </div>
    </>
  );
});

SelectFieldContainer.propTypes = {
  selectedField: fieldPropType.isRequired,
  allFields: PropTypes.arrayOf(fieldPropType).isRequired,
  setFieldAsSelected: PropTypes.func.isRequired,
};

function mapStateToProps(state) {
  return {
    tasksEditMode: state.taskViewPageReducer.tasksEditMode,
    tasksPlanningState: state.taskViewPageReducer.tasksPlanningState,
    tasksPlanningMessage: state.taskViewPageReducer.tasksPlanningMessage,
    loggedInUser: state.appReducer.loggedInUser,
    settings: state.appReducer.settings,
    allFields: state.appReducer.fieldsForDropdown,
    allAreas: state.appReducer.areasForDropdown,
    selectedAreas: state.appReducer.selectedAreas,
    taskTypeGroups: state.appReducer.taskTypeGroupsForDropdown,
    selectedTaskTypeGroups: state.appReducer.selectedTaskTypeGroups,
  };
}

export default connect(mapStateToProps, null)(HeaderPage);
